import { useEffect, useState } from "react";
import { LoadingState } from "@tabipla/ui";

export interface SplashScreenTheme {
  /** 背景グラデーションクラス */
  backgroundGradient: string;
  /** アイコン背景色 */
  iconBg: string;
  /** アイコンテキスト色 */
  iconText: string;
  /** プログレスバーの色 */
  progressColor: string;
}

export interface SplashScreenProps {
  /** アプリ名 */
  appName: string;
  /** タグライン */
  tagline: string;
  /** アイコン (ReactNode) */
  icon: React.ReactNode;
  /** テーマ設定 */
  theme: SplashScreenTheme;
  /** ローディング中かどうか */
  isLoading: boolean;
  /** 最小表示時間 (ms) - デフォルト 500ms */
  minDisplayTime?: number;
  /** スプラッシュ終了時のコールバック */
  onFinish?: () => void;
  /** 子要素（スプラッシュ後に表示するコンテンツ） */
  children: React.ReactNode;
}

const SPLASH_SHOWN_KEY = "splash_shown";

/**
 * 同一セッション内でスプラッシュを表示済みかどうか
 */
function hasShownSplash(): boolean {
  try {
    return sessionStorage.getItem(SPLASH_SHOWN_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * スプラッシュ表示済みフラグを設定
 */
function markSplashShown(): void {
  try {
    sessionStorage.setItem(SPLASH_SHOWN_KEY, "true");
  } catch {
    // sessionStorage not available
  }
}

/**
 * 共通スプラッシュスクリーンコンポーネント
 *
 * - 初回訪問時: スプラッシュを表示（最小表示時間 + ローディング完了まで）
 * - 同一セッション内の再訪問: スプラッシュをスキップ
 *
 * @example
 * <SplashScreen
 *   appName="Waritto"
 *   tagline="割り勘を、もっとスマートに"
 *   icon={<Wallet className="h-8 w-8" />}
 *   theme={{
 *     backgroundGradient: "from-emerald-50 to-white dark:from-emerald-950 dark:to-gray-900",
 *     iconBg: "bg-emerald-500",
 *     iconText: "text-white",
 *     progressColor: "bg-emerald-500",
 *   }}
 *   isLoading={isAuthChecking}
 * >
 *   <App />
 * </SplashScreen>
 */
export function SplashScreen({
  appName,
  tagline,
  icon,
  theme,
  isLoading,
  minDisplayTime = 500,
  onFinish,
  children,
}: SplashScreenProps) {
  const [showSplash, setShowSplash] = useState(() => !hasShownSplash());
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);

  // 最小表示時間のタイマー
  useEffect(() => {
    if (!showSplash) return;

    const timer = setTimeout(() => {
      setMinTimeElapsed(true);
    }, minDisplayTime);

    return () => clearTimeout(timer);
  }, [showSplash, minDisplayTime]);

  // スプラッシュ終了判定
  useEffect(() => {
    if (showSplash && minTimeElapsed && !isLoading) {
      markSplashShown();
      setShowSplash(false);
      onFinish?.();
    }
  }, [showSplash, minTimeElapsed, isLoading, onFinish]);

  // スプラッシュをスキップ（同一セッション内）
  // ただし、isLoading が true の場合は認証チェック中なので待機
  if (!showSplash) {
    if (isLoading) {
      // 認証チェック中はイラスト付きローディングを表示
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
          <LoadingState message="読み込み中..." />
        </div>
      );
    }
    return <>{children}</>;
  }

  return (
    <div
      className={`fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-b ${theme.backgroundGradient}`}
    >
      {/* ロゴとアプリ名 */}
      <div className="animate-fade-in flex flex-col items-center">
        {/* アイコン */}
        <div
          className={`mb-6 flex h-20 w-20 items-center justify-center rounded-3xl shadow-lg ${theme.iconBg} ${theme.iconText}`}
        >
          {icon}
        </div>

        {/* アプリ名 */}
        <h1 className="mb-2 text-3xl font-bold tracking-tight">{appName}</h1>

        {/* タグライン */}
        <p className="text-muted-foreground">{tagline}</p>

        {/* プログレスバー */}
        <div className="mt-8 h-1 w-32 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
          <div
            className={`h-full animate-pulse rounded-full ${theme.progressColor}`}
            style={{
              width: minTimeElapsed && !isLoading ? "100%" : "60%",
              transition: "width 0.3s ease-out",
            }}
          />
        </div>
      </div>
    </div>
  );
}
